import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import taskService from '../../services/taskService';

const TaskForm = ({ 
  task = null, 
  onSave, 
  onCancel, 
  isModal = false,
  availableUsers = []
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: '',
    priority: 'medium',
    category: '',
    assigned_to: '',
    candidate_id: '',
    interview_id: '',
    job_id: '',
    due_date: '',
    estimated_duration: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Initialize form data
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        task_type: task.task_type || '',
        priority: task.priority || 'medium',
        category: task.category || '',
        assigned_to: task.assigned_to || '',
        candidate_id: task.candidate_id || '',
        interview_id: task.interview_id || '',
        job_id: task.job_id || '',
        due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
        estimated_duration: task.estimated_duration || '',
        notes: task.notes || ''
      });
    }
  }, [task]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Task title is required';
    } else if (formData.title.length > 500) {
      newErrors.title = 'Task title must not exceed 500 characters';
    }

    if (formData.description && formData.description.length > 2000) {
      newErrors.description = 'Description must not exceed 2000 characters';
    }

    if (!formData.task_type) {
      newErrors.task_type = 'Task type is required';
    }

    if (!formData.priority) {
      newErrors.priority = 'Priority is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.assigned_to) {
      newErrors.assigned_to = 'Task must be assigned to someone';
    }

    if (formData.due_date && new Date(formData.due_date) < new Date().setHours(0, 0, 0, 0)) {
      newErrors.due_date = 'Due date cannot be in the past';
    }

    if (formData.estimated_duration) {
      const duration = parseInt(formData.estimated_duration);
      if (isNaN(duration) || duration < 1 || duration > 10080) {
        newErrors.estimated_duration = 'Estimated duration must be between 1 and 10080 minutes';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});
    try {
      // Prepare payload
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        estimated_duration: formData.estimated_duration ? parseInt(formData.estimated_duration) : null,
        candidate_id: formData.candidate_id || null,
        interview_id: formData.interview_id || null,
        job_id: formData.job_id || null
      };

      // Remove empty string values
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') {
          payload[key] = null;
        }
      });

      let result;
      if (task) {
        result = await taskService.updateTask(task.id, payload);
        setSuccessMessage('Task updated successfully!');
      } else {
        result = await taskService.createTask(payload);
        setSuccessMessage('Task created successfully!');
      }

      // Show success state
      setSuccess(true);
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage('');
      }, 5000);

      // Reset form if creating new task
      if (!task) {
        resetForm();
      }

      if (onSave) {
        onSave(result.task);
      }
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      task_type: '',
      priority: 'medium',
      category: '',
      assigned_to: '',
      candidate_id: '',
      interview_id: '',
      job_id: '',
      due_date: '',
      estimated_duration: '',
      notes: ''
    });
    setErrors({});
    setSuccess(false);
    setSuccessMessage('');
  };

  // Handle cancel
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const containerClass = isModal 
    ? "space-y-6" 
    : "bg-card border border-border rounded-lg p-6 space-y-6";

  return (
    <div className={containerClass}>
      {!isModal && (
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">
            {task ? 'Edit Task' : 'Create New Task'}
          </h2>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-2">
              Task Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter task title"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.title ? 'border-red-300' : 'border-border'
              }`}
              maxLength={500}
            />
            {errors.title && (
              <p className="text-red-600 text-sm mt-1">{errors.title}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Task Type *
            </label>
            <select
              name="task_type"
              value={formData.task_type}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.task_type ? 'border-red-300' : 'border-border'
              }`}
            >
              <option value="">Select task type</option>
              {taskService.getTaskTypes().map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.task_type && (
              <p className="text-red-600 text-sm mt-1">{errors.task_type}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Priority *
            </label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.priority ? 'border-red-300' : 'border-border'
              }`}
            >
              {taskService.getPriorityLevels().map(priority => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
            {errors.priority && (
              <p className="text-red-600 text-sm mt-1">{errors.priority}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Category *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.category ? 'border-red-300' : 'border-border'
              }`}
            >
              <option value="">Select category</option>
              {taskService.getTaskCategories().map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="text-red-600 text-sm mt-1">{errors.category}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Assign To *
            </label>
            <select
              name="assigned_to"
              value={formData.assigned_to}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.assigned_to ? 'border-red-300' : 'border-border'
              }`}
            >
              <option value="">Select assignee</option>
              {availableUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.role}) - {user.email}
                </option>
              ))}
            </select>
            {errors.assigned_to && (
              <p className="text-red-600 text-sm mt-1">{errors.assigned_to}</p>
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter task description"
            rows={4}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.description ? 'border-red-300' : 'border-border'
            }`}
            maxLength={2000}
          />
          {errors.description && (
            <p className="text-red-600 text-sm mt-1">{errors.description}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {formData.description.length}/2000 characters
          </p>
        </div>

        {/* Timing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Due Date
            </label>
            <input
              type="date"
              name="due_date"
              value={formData.due_date}
              onChange={handleInputChange}
              min={new Date().toISOString().split('T')[0]}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.due_date ? 'border-red-300' : 'border-border'
              }`}
            />
            {errors.due_date && (
              <p className="text-red-600 text-sm mt-1">{errors.due_date}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Estimated Duration (minutes)
            </label>
            <input
              type="number"
              name="estimated_duration"
              value={formData.estimated_duration}
              onChange={handleInputChange}
              placeholder="e.g., 60"
              min="1"
              max="10080"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.estimated_duration ? 'border-red-300' : 'border-border'
              }`}
            />
            {errors.estimated_duration && (
              <p className="text-red-600 text-sm mt-1">{errors.estimated_duration}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Optional: Estimated time to complete this task
            </p>
          </div>
        </div>

        {/* Related Entities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Related Candidate ID
            </label>
            <input
              type="text"
              name="candidate_id"
              value={formData.candidate_id}
              onChange={handleInputChange}
              placeholder="UUID (optional)"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-muted-foreground mt-1">
              If this task relates to a specific candidate
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Related Interview ID
            </label>
            <input
              type="text"
              name="interview_id"
              value={formData.interview_id}
              onChange={handleInputChange}
              placeholder="UUID (optional)"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-muted-foreground mt-1">
              If this task relates to a specific interview
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Related Job ID
            </label>
            <input
              type="text"
              name="job_id"
              value={formData.job_id}
              onChange={handleInputChange}
              placeholder="UUID (optional)"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-muted-foreground mt-1">
              If this task relates to a specific job posting
            </p>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Additional notes or instructions"
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={20} className="text-red-600 mr-3" />
              <div>
                <h3 className="text-red-800 font-medium">Error</h3>
                <p className="text-red-700 text-sm mt-1">{errors.submit}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="CheckCircle" size={20} className="text-green-600 mr-3" />
              <div>
                <h3 className="text-green-800 font-medium">Success</h3>
                <p className="text-green-700 text-sm mt-1">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <div className="flex items-center space-x-3">
            <Button
              type="submit"
              disabled={loading}
              className="min-w-[120px]"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  {task ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Icon name={task ? "Save" : "Plus"} size={16} className="mr-2" />
                  {task ? 'Update Task' : 'Create Task'}
                </>
              )}
            </Button>
            
            {!task && success && (
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="min-w-[120px]"
              >
                <Icon name="Plus" size={16} className="mr-2" />
                Create Another
              </Button>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default TaskForm;